/**
 * Searchspring API Helper Module
 * Provides methods for interacting with Searchspring APIs including search,
 * autocomplete, suggestions, and trending terms.
 *
 * Features:
 * - Session storage caching with 5-minute TTL
 * - UUID-based user/session tracking via cookies
 * - Debounced API calls
 * - Error handling with fallbacks
 *
 * @typedef {{ enabled?: boolean, siteId?: string, siteIdFr?: string, locale?: string, resultsPerPage?: number, autocompleteProducts?: number, moneyFormat?: string, routes?: { search?: string }, [key: string]: unknown }} SearchspringConfig
 * @typedef {{ userId?: string, sessionId?: string, pageLoadId?: string, domain?: string }} Identifiers
 * @typedef {{ uid?: string, id?: string, handle?: string, url?: string, name?: string, title?: string, brand?: string, vendor?: string, product_type_unigram?: string, type?: string, price?: number|string, msrp?: number|string, compare_at_price?: number|string, available?: boolean, ss_in_stock?: string, imageUrl?: string, thumbnailImageUrl?: string, images?: string[], ss_tags?: string[], tags?: string[], sku?: string, variant_id?: string, intellisuggestData?: unknown, intellisuggestSignature?: string }} SearchspringRawProduct
 * @typedef {{ field?: string, label?: string, type?: string, multiple?: boolean, collapsed?: boolean, values?: Array<{ value?: string, label?: string, count?: number, active?: boolean, low?: number, high?: number, color?: string, image?: string }> }} SearchspringRawFacet
 * @typedef {{ value?: string, label?: string, count?: number }} FormattedFacetValue
 * @typedef {{ field: string, label: string, type: string, multiple: boolean, collapsed: boolean, values: FormattedFacetValue[] }} FormattedFacet
 * @typedef {{ currentPage?: number, page?: number, totalPages?: number, totalResults?: number, perPage?: number, resultsPerPage?: number, previousPage?: number|null, nextPage?: number|null }} SearchspringRawPagination
 * @typedef {{ redirected?: boolean, redirectUrl?: string, results?: unknown[], facets?: SearchspringRawFacet[], pagination?: SearchspringRawPagination|null, sorting?: { current?: string, options?: unknown[] }, merchandising?: unknown, filterSummary?: unknown[], search?: { query?: string, correctedQuery?: string }, didYouMean?: unknown[] }} SearchspringRawResponse
 */

const SearchspringAPI = (function () {
  'use strict';

  // Cache configuration
  const CACHE_DURATION_MS = 300000; // 5 minutes
  const CACHE_PREFIX = 'ss-';

  // Cookie names for tracking
  const USER_ID_COOKIE = 'ssUserId';
  const SESSION_ID_COOKIE = 'ssSessionIdNamespace';

  // API endpoints
  const ENDPOINTS = {
    search: 'api/search/search',
    autocomplete: 'api/search/autocomplete',
    suggest: 'api/suggest/query',
    trending: 'api/suggest/trending',
  };

  /**
   * Get configuration from global window object
   * @returns {SearchspringConfig} Searchspring configuration
   */
  function getConfig() {
    return /** @type {SearchspringConfig} */ (window.searchspringConfig || { enabled: false });
  }

  /**
   * Check if Searchspring is enabled
   * @returns {boolean}
   */
  function isEnabled() {
    const config = getConfig();
    return Boolean(config.enabled && config.siteId);
  }

  /**
   * Get the appropriate site ID based on locale
   * @returns {string} Site ID
   */
  function getSiteId() {
    const config = getConfig();
    const locale = config.locale || 'en';

    // Use French site ID if available and locale is French
    if (locale.startsWith('fr') && config.siteIdFr) {
      return config.siteIdFr;
    }

    return config.siteId || '';
  }

  /**
   * Generate a UUID v4
   * @returns {string} UUID string
   */
  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null
   */
  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? (match[2] ?? null) : null;
  }

  /**
   * Set a cookie
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} [days] - Expiration in days (omit for session cookie)
   */
  function setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + value + expires + '; path=/; SameSite=Lax';
  }

  /**
   * Get or create user and session identifiers for tracking
   * @returns {Identifiers}
   */
  function getIdentifiers() {
    let userId = getCookie(USER_ID_COOKIE);
    let sessionId = getCookie(SESSION_ID_COOKIE);

    if (!userId) {
      userId = generateUUID();
      setCookie(USER_ID_COOKIE, userId, 365);
    }

    if (!sessionId) {
      sessionId = generateUUID();
      setCookie(SESSION_ID_COOKIE, sessionId); // Session cookie
    }

    return {
      userId,
      sessionId,
      pageLoadId: generateUUID(),
      domain: window.location.origin + window.location.pathname,
    };
  }

  /**
   * Get cached API response from session storage
   * @param {string} key - Cache key
   * @returns {Object|null} Cached data or null if expired/not found
   */
  function getFromCache(key) {
    try {
      const cached = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!cached) return null;

      const { data, expiry } = JSON.parse(cached);
      if (Date.now() > expiry) {
        sessionStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }

  /**
   * Store API response in session storage cache
   * @param {string} key - Cache key
   * @param {Object} data - Data to cache
   */
  function setToCache(key, data) {
    try {
      sessionStorage.setItem(
        CACHE_PREFIX + key,
        JSON.stringify({
          data,
          expiry: Date.now() + CACHE_DURATION_MS,
        }),
      );
    } catch {
      // Storage full or disabled - fail silently
    }
  }

  /**
   * Generate a cache key from parameters
   * @param {string} endpoint - API endpoint
   * @param {URLSearchParams} params - Request parameters
   * @returns {string} Cache key
   */
  function generateCacheKey(endpoint, params) {
    const config = getConfig();
    const sortedParams = new URLSearchParams([...params.entries()].sort());
    // Include locale and currency so switching country invalidates cached responses
    return `${config.locale || ''}-${config.currency || ''}-${endpoint}-${sortedParams.toString()}`;
  }

  /**
   * Build API URL
   * @param {string} endpoint - API endpoint
   * @param {string} [siteId] - Optional site ID override
   * @returns {string} Full API URL
   */
  function buildApiUrl(endpoint, siteId) {
    const id = siteId || getSiteId();
    return `https://${id}.a.searchspring.io/${endpoint}`;
  }

  /**
   * Make an API request to Searchspring
   * @param {string} endpoint - API endpoint
   * @param {URLSearchParams} params - Request parameters
   * @param {Object} [options] - Additional options
   * @param {boolean} [options.skipCache] - Skip cache lookup
   * @param {boolean} [options.addTracking] - Add tracking identifiers
   * @param {boolean} [options.checkRedirect] - When true, check merchandising.redirect and return redirect data. Only pass for full-search calls, not autocomplete.
   * @returns {Promise<Object|null>} API response or null on error
   */
  async function apiRequest(endpoint, params, options = {}) {
    const { skipCache = false, addTracking = true, checkRedirect = false } = options;
    const siteId = getSiteId();

    // Add site ID to params
    params.set('siteId', siteId);

    // Generate cache key before adding per-request tracking identifiers.
    // pageLoadId is a fresh UUID on every call — including it would make the
    // cache key unique every time and prevent any cache hits.
    const cacheKey = generateCacheKey(endpoint, params);
    if (!skipCache) {
      const cached = getFromCache(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Add tracking identifiers for search/autocomplete (after cache check so
    // they don't pollute the cache key)
    if (addTracking) {
      const ids = getIdentifiers();
      params.set('userId', ids.userId ?? '');
      params.set('sessionId', ids.sessionId ?? '');
      params.set('pageLoadId', ids.pageLoadId ?? '');
      params.set('domain', ids.domain ?? '');
    }

    // Make API request
    const url = buildApiUrl(endpoint, siteId);

    try {
      const response = await fetch(`${url}?${params.toString()}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      // Handle redirects — both HTTP-level (redirectResponse=direct) and JSON-body
      // (redirectResponse=full/minimal). Gated behind checkRedirect so that autocomplete
      // calls while the user is typing never trigger a navigation. Only the explicit
      // search() call passes checkRedirect: true.
      if (checkRedirect && response.redirected) {
        return {
          redirected: true,
          redirectUrl: response.url,
        };
      }

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();

      if (checkRedirect && data?.merchandising?.redirect) {
        return {
          redirected: true,
          redirectUrl: data.merchandising.redirect,
        };
      }

      // Cache successful responses
      setToCache(cacheKey, data);

      return data;
    } catch (error) {
      console.error('[Searchspring] API Error:', error);
      return null;
    }
  }

  /**
   * Perform a search query
   * @param {{ query?: string, page?: number, resultsPerPage?: number, collectionHandle?: string, filters?: string[], sort?: { field?: string, direction?: string }, redirectResponse?: string }} [options] - Search options
   * @returns {Promise<Object|null>} Search results or null on error
   */
  async function search(options = {}) {
    if (!isEnabled()) {
      console.warn('[Searchspring] Not enabled');
      return null;
    }

    const config = getConfig();
    const {
      query = '',
      page = 1,
      resultsPerPage = config.resultsPerPage || 24,
      collectionHandle = '',
      filters = [],
      sort = null,
      redirectResponse = 'full',
    } = options;

    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      resultsPerPage: resultsPerPage.toString(),
      resultsFormat: 'json',
      redirectResponse,
    });

    // Add background filters for collection pages
    if (collectionHandle) {
      params.append('bgfilter.ss_collection', collectionHandle);
      params.append('bgfilter.ss_visibility', 'collection');
    }

    // Add user-selected filters
    filters.forEach((filter) => {
      const [key, value] = filter.split('=');
      if (key && value) {
        params.append(key, decodeURIComponent(value));
      }
    });

    // Add sorting
    const sortConfig = /** @type {{ field?: string, direction?: string } | null} */ (sort);
    if (sortConfig?.field) {
      params.append(`sort.${sortConfig.field}`, sortConfig.direction || 'asc');
    }

    return apiRequest(ENDPOINTS.search, params, { checkRedirect: true });
  }

  /**
   * Perform an autocomplete search for predictive search
   * @param {string} query - Search query
   * @param {Object} [options] - Additional options
   * @param {number} [options.resultsPerPage] - Number of results
   * @returns {Promise<Object|null>} Autocomplete results
   */
  async function autocomplete(query, options = {}) {
    if (!isEnabled() || !query.trim()) {
      return null;
    }

    const config = getConfig();
    const { resultsPerPage = config.autocompleteProducts || 8 } = options;

    const params = new URLSearchParams({
      q: query,
      resultsPerPage: resultsPerPage.toString(),
      resultsFormat: 'json',
    });

    return apiRequest(ENDPOINTS.autocomplete, params);
  }

  /**
   * Get search suggestions based on input
   * @param {string} query - Partial search query
   * @returns {Promise<Object|null>} Suggested search terms
   */
  async function getSuggestions(query) {
    if (!isEnabled() || !query.trim()) {
      return null;
    }

    const params = new URLSearchParams({
      q: query,
    });

    return apiRequest(ENDPOINTS.suggest, params, { addTracking: true });
  }

  /**
   * Get trending search terms
   * @returns {Promise<Object|null>} Trending search terms
   */
  async function getTrending() {
    if (!isEnabled()) {
      return null;
    }

    const params = new URLSearchParams();

    return apiRequest(ENDPOINTS.trending, params, { addTracking: false });
  }

  /**
   * Escape HTML special characters (for use in buildTrendingHtml and similar).
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Build HTML for trending search terms. Shared by predictive search and search results no-results state.
   * @param {string[] | Array<{ searchQuery?: string, query?: string, term?: string }>} terms - Search terms to display as links
   * @param {{ title?: string, searchUrl?: string, variant?: 'predictive' | 'no-results' }} [options] - title, search base URL, and markup variant (classes/roles)
   * @returns {string} HTML string for the trending block
   */
  function buildTrendingHtml(terms, options = {}) {
    if (!terms?.length) return '';
    const config = getConfig();
    const searchUrl = options.searchUrl ?? config?.routes?.search ?? '/search';
    const title = options.title ?? 'Popular searches';
    const variant = options.variant ?? 'no-results';

    const normalizedTerms = terms.map(
      (/** @type {string | { searchQuery?: string, query?: string, term?: string }} */ t) =>
        typeof t === 'string' ? t : (t?.searchQuery ?? t?.query ?? t?.term ?? String(t)),
    );

    if (variant === 'predictive') {
      return `
        <div class="ss-predictive-search__trending">
          <h3 class="ss-predictive-search__section-title h2">${escapeHtml(title)}</h3>
          <ul class="ss-predictive-search__trending-list" role="listbox">
            ${normalizedTerms
              .map(
                (term, index) =>
                  `<li class="ss-predictive-search__trending-item" role="option" data-query="${escapeHtml(term)}" data-index="${index}">
                <a href="${searchUrl}?q=${encodeURIComponent(term)}" class="ss-predictive-search__trending-link">
                  <span class="ss-predictive-search__trending-text">${escapeHtml(term)}</span>
                </a>
              </li>`,
              )
              .join('')}
          </ul>
        </div>
      `;
    }

    return `
      <div class="ss-no-results__trending">
        <h3 class="ss-no-results__trending-title h2">${escapeHtml(title)}</h3>
        <ul class="ss-no-results__trending-list" role="list">
          ${normalizedTerms
            .map(
              (term) =>
                `<li class="ss-no-results__trending-item">
                <a href="${searchUrl}?q=${encodeURIComponent(term)}" class="ss-no-results__trending-link">${escapeHtml(term)}</a>
              </li>`,
            )
            .join('')}
        </ul>
      </div>
    `;
  }

  /**
   * Format a Searchspring product for display
   * @param {SearchspringRawProduct} product - Raw product from Searchspring
   * @returns {Object} Formatted product object
   */
  function formatProduct(product) {
    const config = getConfig();

    return {
      id: product.uid || product.id,
      handle: product.handle || product.url?.split('/').pop(),
      title: product.name || product.title,
      url: product.url,
      vendor: product.brand || product.vendor,
      productType: product.product_type_unigram || product.type,
      price: parseFloat(String(product.price ?? 0)) || 0,
      compareAtPrice:
        parseFloat(String(product.msrp ?? 0)) ||
        parseFloat(String(product.compare_at_price ?? 0)) ||
        null,
      available: product.available !== false && product.ss_in_stock !== '0',
      imageUrl: product.imageUrl || product.thumbnailImageUrl,
      thumbnailUrl: product.thumbnailImageUrl || product.imageUrl,
      images: product.images || [product.imageUrl],
      tags: product.ss_tags || product.tags || [],
      sku: product.sku,
      variantId: product.variant_id,
      // Formatted price using shop money format
      formattedPrice: formatMoney(product.price ?? 0, config.moneyFormat ?? '${{amount}}'),
      formattedCompareAtPrice:
        product.msrp != null
          ? formatMoney(String(product.msrp), config.moneyFormat ?? '${{amount}}')
          : null,
      // Additional Searchspring fields
      intellisuggestData: product.intellisuggestData,
      intellisuggestSignature: product.intellisuggestSignature,
    };
  }

  /**
   * Format a price using Shopify money format
   * @param {number|string} cents - Price in cents or dollars
   * @param {string} format - Shopify money format string
   * @returns {string} Formatted price
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = parseFloat(cents.replace(/[^0-9.-]/g, ''));
    }

    // Assume price is in dollars if it has decimals
    const value = /** @type {number} */ (cents);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    /**
     * @param {number} number
     * @param {number} [precision]
     * @param {string} [thousands]
     * @param {string} [decimal]
     */
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0';
      }

      const fixedNumber = number.toFixed(precision);
      const parts = fixedNumber.split('.');
      const dollars = (parts[0] ?? '').replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    }

    let formattedValue = '';
    const placeholder = formatString.match(placeholderRegex)?.[1];
    switch (placeholder) {
      case 'amount':
        formattedValue = formatWithDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        formattedValue = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formattedValue = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedValue = formatWithDelimiters(value, 0, '.', ',');
        break;
      default:
        formattedValue = formatWithDelimiters(value, 2);
    }

    const re = placeholderRegex;
    return formatString.replace(re, formattedValue);
  }

  /**
   * Format facet data from Searchspring response
   * @param {SearchspringRawFacet} facet - Raw facet from Searchspring
   * @returns {FormattedFacet} Formatted facet object
   */
  function formatFacet(facet) {
    return {
      field: facet.field || '',
      label: facet.label || facet.field || '',
      type: facet.type || 'list',
      multiple: facet.multiple !== false,
      collapsed: facet.collapsed || false,
      values: (facet.values || []).map(
        (
          /** @type {{ value?: string, label?: string, count?: number, active?: boolean, low?: number, high?: number, color?: string, image?: string }} */ value,
        ) => ({
          value: value.value,
          label: value.label || value.value,
          count: value.count || 0,
          active: value.active || false,
          low: value.low, // For range facets
          high: value.high, // For range facets
          color: value.color, // For color swatch facets
          image: value.image, // For visual facets
        }),
      ),
    };
  }

  /**
   * Format pagination data from Searchspring response
   * @param {SearchspringRawPagination|null|undefined} pagination - Raw pagination from Searchspring
   * @returns {Object|null} Formatted pagination object or null
   */
  function formatPagination(pagination) {
    if (!pagination) return null;

    return {
      currentPage: pagination.currentPage || pagination.page || 1,
      totalPages:
        pagination.totalPages ||
        Math.ceil(
          (pagination.totalResults || 0) / (pagination.perPage || pagination.resultsPerPage || 24),
        ) ||
        1,
      totalResults: pagination.totalResults || 0,
      perPage: pagination.perPage || pagination.resultsPerPage || 24,
      previousPage:
        pagination.previousPage ??
        (pagination.currentPage && pagination.currentPage > 1 ? pagination.currentPage - 1 : null),
      nextPage:
        pagination.nextPage ??
        (pagination.currentPage &&
        pagination.totalPages &&
        pagination.currentPage < pagination.totalPages
          ? pagination.currentPage + 1
          : null),
    };
  }

  /**
   * Extract category values from the tags_category facet (product tag/category data from the index).
   * @param {FormattedFacet[]} facets - Formatted facets from the response
   * @returns {Array<{ value: string, count: number }>}
   */
  function getCategoriesFromFacets(facets) {
    if (!Array.isArray(facets)) return [];
    const categoryFacet = facets.find((f) => f.field === 'tags_category');
    if (!categoryFacet?.values?.length) return [];
    return categoryFacet.values
      .map((/** @type {FormattedFacetValue} */ v) => ({
        value: v.value || v.label || '',
        count: v.count || 0,
      }))
      .filter((/** @type {{ value: string, count: number }} */ c) => c.value);
  }

  /**
   * Format complete search response
   * @param {SearchspringRawResponse|null|undefined} response - Raw Searchspring response
   * @returns {SearchspringFormattedResponse | null} Formatted response or null
   */
  function formatSearchResponse(response) {
    if (!response) return null;

    // Handle redirects
    if (response.redirected) {
      return /** @type {SearchspringFormattedResponse} */ (
        /** @type {unknown} */ ({
          redirected: true,
          redirectUrl: response.redirectUrl,
        })
      );
    }

    const facets = (response.facets || []).map((/** @type {SearchspringRawFacet} */ f) =>
      formatFacet(f),
    );

    return /** @type {SearchspringFormattedResponse} */ ({
      products: (response.results || []).map(formatProduct),
      facets,
      categories: getCategoriesFromFacets(facets),
      pagination: formatPagination(response.pagination),
      sorting: {
        current: response.sorting?.current,
        options: response.sorting?.options || [],
      },
      merchandising: response.merchandising || {},
      filterSummary: response.filterSummary || [],
      query: response.search?.query || '',
      correctedQuery: response.search?.correctedQuery,
      didYouMean: response.didYouMean || [],
    });
  }

  /**
   * Clear all Searchspring cache entries from session storage
   */
  function clearCache() {
    try {
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // Storage not available
    }
  }

  /**
   * Create a debounced version of a function
   * @param {(...args: any[]) => void} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {(...args: any[]) => void} Debounced function
   */
  function debounce(func, wait) {
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timeout;
    return function executedFunction(/** @type {any[]} */ ...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Public API
  return {
    // Core methods
    search,
    autocomplete,
    getSuggestions,
    getTrending,

    // Utility methods
    isEnabled,
    getConfig,
    getSiteId,
    getIdentifiers,
    clearCache,
    debounce,
    escapeHtml,
    buildTrendingHtml,

    // Formatting methods
    formatProduct,
    formatFacet,
    formatPagination,
    formatSearchResponse,
    formatMoney,
  };
})();

// Expose to global scope for use by other scripts
window.SearchspringAPI = SearchspringAPI;

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SearchspringAPI;
}
